import React, { useRef, useState } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

interface MultiImageAddProps {
  /** 현재 이미지 URL 배열 */
  imageUrls?: string[];
  /** 이미지 아이템 크기 (기본값: 120px) */
  itemSize?: number;
  /** 최대 이미지 개수 */
  maxImages?: number;
  /** 이미지 파일이 선택되었을 때 호출되는 콜백 */
  onImageAdd?: (file: File) => void;
  /** 여러 이미지 파일이 선택되었을 때 호출되는 콜백 */
  onImagesAdd?: (files: File[]) => void;
  /** 이미지가 삭제되었을 때 호출되는 콜백 */
  onImageDelete?: (index: number) => void;
  /** 비활성화 상태 */
  disabled?: boolean;
}

const MultiImageAdd: React.FC<MultiImageAddProps> = ({
  imageUrls = [],
  itemSize = 120,
  maxImages = 10,
  onImageAdd,
  onImagesAdd,
  onImageDelete,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());
  const [isDragOver, setIsDragOver] = useState(false);

  const handleAddImageClick = () => {
    if (disabled || imageUrls.length >= maxImages) return;
    fileInputRef.current?.click();
  };

  const processFiles = async (files: File[]) => {
    const remainingSlots = maxImages - imageUrls.length;
    const filesToAdd = files.slice(0, remainingSlots);
    
    if (filesToAdd.length > 0) {
      if (onImagesAdd) {
        // onImagesAdd가 있으면 한 번에 처리 (권장 방식)
        onImagesAdd(filesToAdd);
      } else if (onImageAdd) {
        // onImagesAdd가 없으면 순차적으로 처리하되 Promise로 안전하게 처리
        for (const file of filesToAdd) {
          onImageAdd(file);
          // 각 파일 처리 사이에 약간의 지연을 두어 상태 업데이트가 완료되도록 함
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      await processFiles(fileArray);
    }
    // 같은 파일을 다시 선택할 수 있도록 input value 리셋
    event.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && imageUrls.length < maxImages) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || imageUrls.length >= maxImages) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      await processFiles(imageFiles);
    }
  };

  const handleDeleteClick = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onImageDelete) {
      onImageDelete(index);
    }
    // 해당 인덱스의 에러 상태도 제거
    setImgErrors(prev => {
      const newErrors = new Set(prev);
      newErrors.delete(index);
      return newErrors;
    });
  };

  const handleImageError = (index: number) => {
    devLog(`이미지 로드 실패 - 인덱스: ${index}`);
    setImgErrors(prev => {
      const newErrors = new Set(prev);
      newErrors.add(index);
      return newErrors;
    });
  };

  const canAddMore = imageUrls.length < maxImages;

  return (
    <Container
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      isDragOver={isDragOver}
    >
      <ScrollContainer>
        <ImageList itemSize={itemSize}>
          {/* 기존 이미지들 */}
          {imageUrls.map((imageUrl, index) => (
            <ImageItem key={index} itemSize={itemSize}>
              <ImageContainer
                itemSize={itemSize}
                disabled={disabled}
              >
                {imgErrors.has(index) ? (
                  <ErrorImage 
                    src="/fluent_person.svg" 
                    alt="Default Image" 
                    itemSize={itemSize}
                  />
                ) : (
                  <Image 
                    src={imageUrl} 
                    alt={`Image ${index + 1}`} 
                    itemSize={itemSize}
                    onError={() => handleImageError(index)}
                  />
                )}
                
                {onImageDelete && (
                  <DeleteIconWrapper
                    onClick={(e) => handleDeleteClick(index, e)}
                    itemSize={itemSize}
                  >
                    {FiTrash2({})}
                  </DeleteIconWrapper>
                )}
              </ImageContainer>
            </ImageItem>
          ))}
          
          {/* 이미지 추가 버튼 (항상 가장 오른쪽) */}
          {canAddMore && (
            <ImageItem itemSize={itemSize}>
              <AddImageContainer
                onClick={handleAddImageClick}
                disabled={disabled}
                itemSize={itemSize}
              >
                <PlusIconWrapper itemSize={itemSize * 0.3}>
                  {FiPlus({})}
                </PlusIconWrapper>
              </AddImageContainer>
            </ImageItem>
          )}
        </ImageList>
      </ScrollContainer>
      
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
      />
    </Container>
  );
};

const Container = styled.div<{ isDragOver?: boolean }>`
  width: 100%;
  position: relative;
  transition: all 0.2s ease;
  
  ${props => props.isDragOver && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(59, 130, 246, 0.1);
      border: 2px dashed #3b82f6;
      border-radius: 8px;
      pointer-events: none;
      z-index: 10;
    }
  `}
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd transparent;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background-color: #999999;
  }
`;

const ImageList = styled.div<{ itemSize: number }>`
  display: flex;
  gap: 12px;
  padding: 8px 0;
  min-height: ${props => props.itemSize + 16}px;
`;

const ImageItem = styled.div<{ itemSize: number }>`
  flex-shrink: 0;
  width: ${props => props.itemSize}px;
  height: ${props => props.itemSize}px;
`;

const ImageContainer = styled.div<{ 
  disabled: boolean; 
  itemSize: number; 
}>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const Image = styled.img<{ itemSize: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const ErrorImage = styled.img<{ itemSize: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  padding: 10px;
`;

const AddImageContainer = styled.div<{ 
  disabled: boolean; 
  itemSize: number; 
}>`
  width: 100%;
  height: 100%;
  background-color: #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 2px dashed #bdbdbd;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    ${props => !props.disabled && `
      background-color: #f0f0f0;
      border-color: #999999;
      transform: scale(1.02);
    `}
  }
`;

const PlusIconWrapper = styled.div<{ itemSize: number }>`
  width: ${props => props.itemSize}px;
  height: ${props => props.itemSize}px;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DeleteIconWrapper = styled.div<{ itemSize: number }>`
  position: absolute;
  top: ${props => props.itemSize * 0.05}px;
  right: ${props => props.itemSize * 0.05}px;
  width: ${props => props.itemSize * 0.2}px;
  height: ${props => props.itemSize * 0.2}px;
  color: #e53e3e;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;

  &:hover {
    background-color: #fee;
    border-color: #e53e3e;
    transform: scale(1.1);
  }
  
  svg {
    width: ${props => props.itemSize * 0.12}px;
    height: ${props => props.itemSize * 0.12}px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

export default MultiImageAdd;
